import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useData } from "@/lib/data";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertTriangle, Scissors } from "lucide-react";
import { toast } from "sonner";

const rowKey = (r) => `${r.product_id}|${r.size}`;

export default function Shortage() {
  const { productById } = useData();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/shortage").then((r) => {
      setRows(r.data);
      setSelected(Object.fromEntries(r.data.map((x) => [rowKey(x), true])));
    }).finally(() => setLoading(false));
  }, []);

  const chosen = rows.filter((r) => selected[rowKey(r)]);
  const totalShort = chosen.reduce((a, b) => a + b.shortage, 0);

  const transfer = () => {
    if (!chosen.length) { toast.error("Aktarılacak satır seçin"); return; }
    const byProduct = {};
    for (const r of chosen) {
      byProduct[r.product_id] = byProduct[r.product_id] || {};
      byProduct[r.product_id][r.size] = r.shortage;
    }
    const prefill = Object.entries(byProduct).map(([product_id, sizes]) => ({ product_id, sizes }));
    navigate("/kesim", { state: { prefill } });
  };

  return (
    <div className="p-5 lg:p-10 max-w-[1400px]" data-testid="shortage-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
        <div>
          <p className="text-[11px] font-mono uppercase tracking-widest text-[#B45309] mb-2">Üretim Planı</p>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold uppercase tracking-tight flex items-center gap-3">
            <AlertTriangle className="w-7 h-7 text-[#EF4444]" /> Eksik Üretim
          </h1>
          <p className="text-[#6B5B4A] mt-2 text-sm">Açık siparişlerde olup stokta yetmeyen numaralar</p>
        </div>
        <Button onClick={transfer} disabled={!chosen.length} data-testid="shortage-transfer-btn"
          className="bg-[#D97706] hover:bg-[#B45309] text-white font-display uppercase tracking-wide h-11 px-5">
          <Scissors className="w-4 h-4 mr-2" /> Kesim Listesine Aktar ({totalShort} çift)
        </Button>
      </div>

      <div className="rounded-lg border border-[#DCCFB8] bg-[#FFFBF4] overflow-x-auto" data-testid="shortage-table">
        <table className="w-full text-sm zs-table">
          <thead className="bg-[#EFE6D6] border-b border-[#DCCFB8]">
            <tr>
              <th className="px-4 py-3 w-10"></th>
              <th className="text-left px-4 py-3">Model Kodu</th>
              <th className="text-left px-4 py-3">Renk</th>
              <th className="text-left px-4 py-3">Taban</th>
              <th className="text-center px-4 py-3">N°</th>
              <th className="text-right px-4 py-3">Kalan Sipariş</th>
              <th className="text-right px-4 py-3">Stok</th>
              <th className="text-right px-4 py-3">Eksik</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={8} className="px-4 py-10 text-center text-[#8C7B68]">Yükleniyor...</td></tr>}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-10 text-center text-[#047857]" data-testid="shortage-empty">
                Eksik yok — tüm açık siparişler stokla karşılanabiliyor.
              </td></tr>
            )}
            {rows.map((r) => {
              const p = productById(r.product_id);
              const k = rowKey(r);
              return (
                <tr key={k} className="border-b border-[#E8DDCA] hover:bg-[#EFE6D6]/50" data-testid={`shortage-row-${k}`}>
                  <td className="px-4 py-3">
                    <Checkbox checked={!!selected[k]} data-testid={`shortage-check-${k}`}
                      onCheckedChange={(v) => setSelected((s) => ({ ...s, [k]: !!v }))} />
                  </td>
                  <td className="px-4 py-3 font-mono font-semibold text-[#B45309]">{p?.model_code || "—"}</td>
                  <td className="px-4 py-3">{p?.model_color || "—"}</td>
                  <td className="px-4 py-3 text-[#6B5B4A] text-xs">{p?.sole_code ? `${p.sole_code} / ${p.sole_color}` : "—"}</td>
                  <td className="px-4 py-3 text-center font-mono">{r.size}</td>
                  <td className="px-4 py-3 text-right font-mono">{r.remaining}</td>
                  <td className="px-4 py-3 text-right font-mono text-[#6B5B4A]">{r.stock}</td>
                  <td className="px-4 py-3 text-right font-mono font-bold text-[#EF4444]">{r.shortage}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
